import { AfterViewInit, Component, OnDestroy, OnInit, WritableSignal, inject, signal } from "@angular/core";
import { BarcodeScanner } from "@capacitor-community/barcode-scanner";
import { Device } from "@capacitor/device";
import { Network } from "@capacitor/network";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { skipWhile, take } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { GetScanning } from "./shared/actions/utility.actions";
import { IEvent, IEventUser } from "./shared/interfaces";
import { getContainersState } from "./shared/selectors/containers.selectors";
import { getInit, getLoading, getScanning } from "./shared/selectors/utility.selectors";
import { AuthService, ContainersService, DeviceInfoService, StorageService, UtilityService } from "./shared/services";
import { NotificationsService } from "./shared/services/notifications.service";
import "zone.js/plugins/zone-patch-rxjs";
import { getCurrentEvent } from "./shared/selectors/events.selectors";
import { TextZoom } from "@capacitor/text-zoom";
import { NavigationBar, NavigationBarPluginEvents } from "@hugotomazi/capacitor-navigation-bar";
// import { register } from "swiper/element/bundle";
// register();

@Component({
	selector: "app-root",
	templateUrl: "app.component.html",
	styleUrls: ["app.component.scss"],
	standalone: false
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
	public SUtility = inject(UtilityService);

	inApp: boolean = false;
	eventUser: IEventUser;

	subscriptions: Subscription[] = [];
	onPauseSubscription: Subscription;
	onResumeSubscription: Subscription;
	loader: WritableSignal<boolean> = signal(true);

	isMobile: boolean;
	scanning: boolean = false;

	currentEvent: IEvent;

	onMaintenanceApp: boolean = false;
	logoPlatform =
		environment.firebase.projectId === "invent-app-prd"
			? "/assets/images/invent/logo.png"
			: environment.firebase.projectId === "ceu-app-prd"
				? "/assets/images/ceu/logo.png"
				: "/assets/images/b3/logo.png";

	constructor(
		private STranslate: TranslateService,
		private SContainers: ContainersService,
		private store: Store,
		private SAuth: AuthService,
		private SNotifications: NotificationsService,
		private SStorage: StorageService,
		public platform: Platform,
		private deviceInfoService: DeviceInfoService
	) {
		this.isMobile = this.platform.is("ios") || this.platform.is("android");
		this.initOnApp();
	}

	async initOnApp() {
		try {
			// Initialize StatusBar for Android edge-to-edge
			if (this.platform.is("android")) {
				try {
					// Use debounced listeners to avoid interfering with touch events
					let navigationBarTimeout: any;

					NavigationBar.addListener(NavigationBarPluginEvents.SHOW, () => {
						// Debounce to avoid rapid calls during menu interactions
						if (navigationBarTimeout) {
							clearTimeout(navigationBarTimeout);
						}
					});

					// Multiple attempts to ensure edge-to-edge is properly set
					await StatusBar.setBackgroundColor({ color: "#00000000" });
					await StatusBar.setOverlaysWebView({ overlay: true });
					await StatusBar.setStyle({ style: Style.Light });

					// Force a second configuration to ensure it sticks
					await new Promise((resolve) => setTimeout(resolve, 50));
					await StatusBar.setBackgroundColor({ color: "#00000000" });
					await StatusBar.setOverlaysWebView({ overlay: true });
					await StatusBar.setStyle({ style: Style.Light });
				} catch (error) {
					console.error("Error configuring StatusBar:", error);
				}
			}

			// Reduced delay for better responsiveness while ensuring configurations are applied
			await new Promise((resolve) => setTimeout(resolve, 150));
			await SplashScreen.hide();
			this.manageSplashscreen("show");
			this.resetTextZoomConfig();
			this.manageAppPauseResume();

			this.deviceInfoService.getPlatformInfo();

			if (this.platform.is("mobile") && window.innerWidth < 768 && !this.platform.is("mobileweb")) {
				// window.screen.orientation.lock("portrait");
				// ScreenOrientation.lock({ orientation: "portrait" });
			}
			this.store.select(getScanning).subscribe((scanning) => {
				this.scanning = scanning;

				if (scanning) {
					document.body.style.setProperty(`background-color`, "transparent !important");
				} else {
					document.body.style.setProperty(`background-color`, "inherit");
				}
			});
			this.subscriptions.push(
				this.SUtility.getAppSettings().subscribe({
					next: (settings) => {
						this.onMaintenanceApp =
							settings?.onMaintenanceApp === undefined ? false : settings.onMaintenanceApp;
					},
					error: (error) => {
						console.error("Err: ", error);
					}
				})
			);
			this.setLanguageUse();
			this.SUtility.listenToNetworkStatus();
			this.SUtility.manageFavicon();

			this.initApp();
		} catch (error) {
			console.error("❌ [APP] Error in initOnApp:", error);
		}
	}

	ngOnInit() {
		this.SStorage.init();

		// Get container
		const containerId =
			!environment.platform.containerId || environment.platform.containerId === "defaultContainer"
				? "defaultContainer"
				: environment.platform.containerId;

		this.SContainers.getContainers(containerId);

		this.getContainer();

		// Init auth
		this.SAuth.initAuth();
		// Init my event user
		// this.SEventUsers.initMyEventUser();

		// init notif config
		if (this.isMobile && !this.platform.is("mobileweb")) {
			this.SNotifications.initOneSignal(environment.platform.oneSignalAppId);
		}

		this.subscriptions.push(
			this.store.select(getLoading).subscribe((loading) => {
				this.loader.set(loading);
			})
		);

		this.subscriptions.push(
			this.store.select(getCurrentEvent).subscribe((event) => {
				if (!this.currentEvent || (this.currentEvent && event && this.currentEvent.uid !== event.uid)) {
					this.currentEvent = event;
					this.logo =
						this.currentEvent && this.currentEvent.identity && this.currentEvent.identity.logo
							? this.currentEvent.identity.logo
							: "";
					this.initProgressBar();
				}
			})
		);
	}

	ngAfterViewInit() {
		if (navigator.appVersion.indexOf("Win") != -1) {
			const elements = document.querySelectorAll(".windows_scrollbar");

			elements.forEach((e) => {
				e.setAttribute("class", "custom_scrollbar");
			});
		}
	}

	ngOnDestroy() {
		this.subscriptions
			.concat(this.onPauseSubscription, this.onResumeSubscription)
			.forEach((sub) => sub?.unsubscribe());
		//this.SPresence.setPresence("offline");
	}

	progressBar: boolean = true;
	initProgress: number = 0;
	progressBarSub: Subscription;
	logo: string = "";
	initProgressBar() {
		this.progressBar = false;
		// this.store
		// 	.select(
		// 		checkIfDatasEventReady([
		// 			"initBaseCustomFields",
		// 			"initChats",
		// 			"initDocuments",
		// 			"initEvent",
		// 			"initEventUser",
		// 			"initFolders",
		// 			"initGroups",
		// 			"initImages",
		// 			"initModules",
		// 			"initModulesCustomFields",
		// 			"initNotifications",
		// 			"initSchedules",
		// 			"initSpeakers",
		// 			"initVideos"
		// 		])
		// 	)
		// 	.pipe(
		// 		skipWhile((init) => !init),
		// 		take(1)
		// 	)
		// 	.subscribe(() => {
		// 		this.progressBar = false;
		// 	});

		// if (this.progressBarSub && !this.progressBarSub.closed) {
		// 	this.progressBarSub.unsubscribe();
		// }
		// this.progressBarSub = this.store.select(getInitDatasEvent).subscribe((init) => {
		// 	const step: number =
		// 		1 /
		// 		Object.keys(init).filter((key) =>
		// 			[
		// 				"initBaseCustomFields",
		// 				"initChats",
		// 				"initDocuments",
		// 				"initEvent",
		// 				"initEventUser",
		// 				"initFolders",
		// 				"initGroups",
		// 				"initImages",
		// 				"initModules",
		// 				"initModulesCustomFields",
		// 				"initNotifications",
		// 				"initSchedules",
		// 				"initSpeakers",
		// 				"initVideos"
		// 			].includes(key)
		// 		).length;
		// 	let progress = 0;
		// 	for (const key of Object.keys(init)) {
		// 		if (init[key]) {
		// 			progress += step;
		// 		}
		// 	}
		// 	if (progress > this.initProgress) {
		// 		this.initProgress = progress;
		// 	}
		// });
	}

	manageAppPauseResume() {
		this.platform.ready().then(() => {
			if ((this.platform.is("mobile") && window.innerWidth < 768) || this.platform.is("mobileweb")) {
				this.onPauseSubscription = this.platform.pause.subscribe(() => {
					// App get in background
					this.resetTextZoomConfig();
				});

				this.onResumeSubscription = this.platform.resume.subscribe(() => {
					// if (this.platform.is("android")) {
					// 	this.deviceInfoService.showNavAndStatusBar();
					// }
					// App get in foreground
					this.resetTextZoomConfig();
				});
			}
		});
	}

	async initApp() {
		const status = await Network.getStatus();

		if (status && status.connected) {
			this.store
				.select(getInit)
				.pipe(
					skipWhile((init) => {
						return !init.initAuth || !init.initUser || !init.initContainers;
					}),
					take(1)
				)
				.subscribe(() => {
					// When init down stop spash screen etc
					this.manageSplashscreen("hide");
				});

			// Safety timeout to force splash screen hide after 5 seconds max
			setTimeout(() => {
				this.manageSplashscreen("hide");
			}, 5000);
		} else {
			this.manageSplashscreen("hide");
		}
	}

	async resetTextZoomConfig() {
		if (
			this.platform.is("ios") ||
			this.platform.is("android") ||
			this.platform.is("mobileweb") ||
			this.platform.is("mobile") ||
			this.platform.is("capacitor")
		) {
			try {
				await this.platform.ready();
				if (
					this.platform.is("android") ||
					this.platform.is("ios") ||
					this.platform.is("mobileweb") ||
					this.platform.is("mobile") ||
					this.platform.is("capacitor")
				) {
					await TextZoom.set({
						value: 1.0
					});
					document.body.style.setProperty(`--ion-dynamic-font`, "initial !important");
				}
			} catch (error) {
				console.error("🚀 initTextZoomConfig error =>", error);
			}
		}
	}

	stopScan() {
		this.store.dispatch(GetScanning({ payload: false }));
		// Case scanning running
		BarcodeScanner.showBackground();
		BarcodeScanner.stopScan();

		// Restore NavigationBar after barcode scan
		if (this.platform.is("android")) {
			try {
				NavigationBar.show();
			} catch (error) {
				console.error("Error restoring NavigationBar after scan:", error);
			}
		}
	}

	/**
	 * Setting used language
	 * @param language
	 */
	async setLanguageUse() {
		let language = environment.platform.defaultLanguage;
		if (this.platform.is("ios") || this.platform.is("android")) {
			const langTag = await Device.getLanguageTag();
			if (["fr-FR", "en-US", "pt-BR", "de-DE", "es-ES"].includes(langTag.value)) {
				const lang = langTag.value.slice(0, 2).toLowerCase() + langTag.value.slice(3, 5);
				language = lang.charAt(0).toUpperCase() + lang.slice(1);
			}
		}

		this.STranslate.setDefaultLang(language);
		this.STranslate.use(language);
	}

	/**
	 * Container and init style
	 */
	getContainer() {
		this.subscriptions.push(
			this.store.select(getContainersState).subscribe((containersState) => {
				const container =
					!environment.platform.containerId || !containersState.currentContainer
						? containersState.defaultContainer
						: containersState.currentContainer;
				if (container) {
					/**
					 * Setting default color
					 */
					document.body.style.setProperty(`--ion-color-default`, container.ionicColors.defaultColor);
					document.body.style.setProperty(
						`--ion-color-default-contrast`,
						container.ionicColors.defaultContrastColor
					);
					document.body.style.setProperty(
						`--ion-color-default-shade`,
						container.ionicColors.defaultShadeColor
					);
					document.body.style.setProperty(`--ion-color-default-tint`, container.ionicColors.defaultTintColor);

					/**
					 * Setting colors of login
					 */
					document.body.style.setProperty(`--valid-btn-bg-color`, container.loginColors.validBtnBgColor);
					document.body.style.setProperty(`--valid-btn-color`, container.loginColors.validBtnColor);
					document.body.style.setProperty(`--login-btn-bg-color`, container.loginColors.loginBtnBgColor);
					document.body.style.setProperty(`--login-btn-color`, container.loginColors.loginBtnColor);
					document.body.style.setProperty(
						`--btn-bg-color-hover`,
						`${container.loginColors.validBtnBgColor}d9`
					);
					document.body.style.setProperty(
						`--text-color-1st-login`,
						container.loginColors.loginTextFirstColor
					);
					document.body.style.setProperty(
						`--text-color-2nd-login-desktop`,
						container.loginColors.loginTextSecondColorDesktop
					);
					document.body.style.setProperty(
						`--text-color-2nd-login-mobile`,
						container.loginColors.loginTextSecondColorMobile
					);
					document.body.style.setProperty(`--label-login-color`, container.loginColors.loginTextColor);

					/**
					 * Menu color
					 */
					document.body.style.setProperty(`--menu-background-color`, container.loginColors.loginTextColor);
					document.body.style.setProperty(`--menu-text-color`, container.loginColors.loginTextColor);
					if (navigator.appVersion.indexOf("Win") != -1) {
						if (!document.getElementById("id-custom-scrollbar")) {
							const sheet = document.createElement("style");
							sheet.id = "id-custom-scrollbar";
							sheet.innerHTML =
								// eslint-disable-next-line max-len
								".custom-scrollbar {--offset-bottom: auto !important;--overflow: hidden;overflow: auto;&::-webkit-scrollbar {width: 8px;-ms-overflow-style: none;scrollbar-width: none;scroll-behavior: smooth;}&::-webkit-scrollbar-thumb {background-color: #00000065 !important;border-radius: 12px !important;}}";
							document.body.appendChild(sheet);
						}
					}
				}
				this.loader.set(false);
			})
		);
	}

	/**
	 * Manage splashscreen
	 * @param type
	 */
	async manageSplashscreen(type: string) {
		try {
			const deviceInfo = await Device.getInfo();
			if (
				deviceInfo &&
				deviceInfo.platform &&
				(deviceInfo.platform === "android" || deviceInfo.platform === "ios")
			) {
				if (type === "show") {
					if (this.platform.is("android")) {
						StatusBar.hide();
						// NavigationBar.hide();
					}
					await SplashScreen.show({
						autoHide: false
					});
				} else {
					await SplashScreen.hide();
					if (this.platform.is("android")) {
						StatusBar.show();
					}
				}
			}
		} catch (error) {
			console.error("***** Log Error splashscreen: " + JSON.stringify(error));
		}
	}
}
